import { Component, Input, OnInit } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { ReportFormat } from '@cloud-twin-v2/generated/simulation.generated.client';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { WlmDialogSettings } from '@common-modules/shared/model/dialog/wlm-dialog-setting';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InstanceUpdatesManagerService } from '../../../../../../cloud-twin/src/app/cloud-twin-shared/workers/instance-updates-manager.service';
import { MasterOperatorConnectItemsSettings } from '../../models/master-operator-connect-settings';
import { instanceBelongsToConnectSettings } from '../../operators/instance-belongs-to-settings.operator';
import { MasterOperatorService } from '../../services/master-operator.service';
import { ReportsService } from '../../services/reports.service';

const COMPONENT_SELECTOR = 'tc-report-list';

@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './report-list.component.html',
    styleUrls: ['./report-list.component.scss'],
    standalone: false
})
export class ReportListComponent implements OnInit {
  private _connectSettings: MasterOperatorConnectItemsSettings;

  public get connectSettings(): MasterOperatorConnectItemsSettings {
    return this._connectSettings;
  }
  @Input() public set connectSettings(value: MasterOperatorConnectItemsSettings) {
    this._connectSettings = value;
    if (value) {
      this.loadReports(value);
    }
  }

  isOpen = false;
  instancesReports: InstanceReports[];
  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;

  constructor(
    private _reportsService: ReportsService,
    private _spinnerService: SpinnerService,
    private _dialogService: DialogService,
    private _instanceUpdatesService: InstanceUpdatesManagerService,
    private _masterOperatorService: MasterOperatorService
  ) {}

  ngOnInit(): void {
    this.listenInstanceUpdates();
  }

  dropDown() {
    this.isOpen = !this.isOpen;
  }

  selectReport(reportName: string, instanceId: string) {
    this._spinnerService.setLoading(true, COMPONENT_SELECTOR);

    this._instanceUpdatesService.connectionId$.subscribe({
      next: (connectionId) => {
        this._reportsService
          .getReport(instanceId, connectionId, reportName, ReportFormat.Excel)
          .subscribe({
            next: (report) => {
              // The report call will be done when notified in the thin client worker.
              this._dialogService.showTranslatedMessageInSnackBar(
                new WlmDialogSettings({
                  translateKey: `${this.T_SCOPE}.messages.report-queued`,
                  icon: 'success',
                  timer: 5000,
                })
              );
              this._spinnerService.setLoading(false, COMPONENT_SELECTOR);
            },
            error: this.handleError,
          });
      },
      error: this.handleError,
    });
  }

  private handleError = (error) => {
    this._dialogService.showErrorMessage(error);
    this._spinnerService.setLoading(false, COMPONENT_SELECTOR);
  };

  private listenInstanceUpdates(): void {
    this._instanceUpdatesService.updatedInstance$
      .pipe(
        untilDestroyed(this),
        instanceBelongsToConnectSettings(() => this.connectSettings)
      )
      .subscribe((instance) => {
        // Reload reports, because some instance may have changed status.
        this.loadReports(this.connectSettings);
      });
  }

  private loadReports(connectSettings: MasterOperatorConnectItemsSettings) {
    this.isOpen = false;
    const instanceIds = connectSettings.items.map((x) => x.instanceId);
    this._reportsService.getReportByInstances(instanceIds).subscribe({
      next: (reportsByInstances) => {
        const resultInstanceIds = Object.keys(reportsByInstances.reports);

        const instancesReports: InstanceReports[] = [];
        resultInstanceIds.forEach((instanceId) => {
          const connectionItem = connectSettings.items.find((x) => x.instanceId === instanceId);
          if (connectionItem) {
            const reports = reportsByInstances.reports[instanceId];
            const scenarioProfiles = new InstanceReports({
              name: this._masterOperatorService.instanceReadableLabel(connectionItem),
              instanceId,
              reports,
            });
            instancesReports.push(scenarioProfiles);
          }
        });

        this.instancesReports = instancesReports;
      },
    });
  }
}

export class InstanceReports {
  name: string;
  instanceId: string;
  reports: string[];

  constructor(init: Partial<InstanceReports>) {
    Object.assign(this, init);
  }
}
